/**
 * Story 8.2 (AC9) — client-side search telemetry: visitor session identity, the
 * two-call route→record orchestration plumbing, and event dispatch.
 *
 * All network calls go to the marketplace's OWN same-origin `/api/search-*`
 * proxies (server/api/search-*.js), which inject the server-side internal bearer
 * token before forwarding to tph-api. The browser never holds that token.
 *
 * Everything here is best-effort and SSR-safe: missing `window`/storage or a
 * failed fetch degrades silently and never blocks a buyer's search.
 *
 * Flow:
 *   1. Topbar submit → routeSearch(raw_query) → push the /s URL.
 *      The route response is stashed via setPendingRoute (sessionStorage) so the
 *      post-render record can read it after navigation.
 *   2. SearchPage, after results render → consume the pending route → recordSearch
 *      (with result_count + latency_ms) → setCurrentSearch(search_id).
 *   3. Filter refinements / clicks → recordSearchEvent referencing that search_id.
 *
 * A reload / shared link / pagination has NO pending route (it was never set by a
 * fresh submit), so it re-hydrates from the URL and fires NEITHER /search/route
 * NOR /search/record — no AI call, no new search_log row (AC9).
 */

import { apiBaseUrl } from './api';

const VISITOR_SESSION_KEY = 'tph_visitor_session_id';
const PENDING_ROUTE_KEY = 'tph_pending_search_route';
const CURRENT_SEARCH_KEY = 'tph_current_search';

// A pending route older than this (ms) is treated as abandoned and ignored.
const PENDING_ROUTE_TTL_MS = 5 * 60 * 1000;

const hasWindow = () => typeof window !== 'undefined';

/** RFC4122 v4 UUID, preferring the platform crypto when available. */
export const generateUuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * The durable per-visitor handle (architecture §2.7): generated once, persisted
 * in localStorage, reused on every /search/route and /search/record call.
 * Sent regardless of auth state. This is the 8.2-owned client piece deferred
 * from Story 8.1.
 */
export const getVisitorSessionId = () => {
  if (!hasWindow()) {
    return null;
  }
  try {
    let id = window.localStorage.getItem(VISITOR_SESSION_KEY);
    if (!id) {
      id = generateUuid();
      window.localStorage.setItem(VISITOR_SESSION_KEY, id);
    }
    return id;
  } catch (e) {
    // localStorage blocked (private mode / disabled cookies) — still attribute
    // this single search to a fresh id rather than dropping the session field.
    return generateUuid();
  }
};

/**
 * Ticket 88 Part 1 (ledger #1376) — logout starts a fresh visitor. Removes the
 * stored id; the next `getVisitorSessionId()` mints a new one lazily, exactly as
 * a first-time visitor's. Never throws: a blocked localStorage has nothing to
 * remove. Called from `src/util/visitorLink.js` (`resetVisitorIdentity`), which
 * `logoutThunk` runs on success.
 */
export const resetVisitorSessionId = () => {
  if (!hasWindow()) {
    return;
  }
  try {
    window.localStorage.removeItem(VISITOR_SESSION_KEY);
  } catch (e) {
    // localStorage blocked — nothing stored, nothing to remove.
  }
};

const postJson = async (path, body) => {
  if (!hasWindow() || typeof window.fetch !== 'function') {
    return null;
  }
  // Resolve through apiBaseUrl() (mirrors src/util/api.js / Story 11.4 dev relay)
  // so in local dev these hit the API server on :3500 rather than webpack-dev-server
  // on :3000 (which 404s and fails the call open to plain keyword). Same-origin in prod.
  const response = await window.fetch(`${apiBaseUrl()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`tph search proxy ${path} responded ${response.status}`);
  }
  return response.json();
};

/**
 * POST /api/search-route — the sync routing call (no DB write, no search_id).
 * @returns {Promise<{routed_to, routing_reason, parsed_filters, unmatched_keywords, route_context}>}
 */
export const routeSearch = (rawQuery, { userUuid } = {}) => {
  const body = { raw_query: rawQuery, visitor_session_id: getVisitorSessionId() };
  if (userUuid) {
    body.user_uuid = userUuid;
  }
  return postJson('/api/search-route', body);
};

/**
 * POST /api/search-record — the single deferred search_log write.
 * @returns {Promise<{search_id}>}
 */
export const recordSearch = params => postJson('/api/search-record', params);

/**
 * POST /api/search-events — fire-and-forget telemetry (202). Caller supplies a
 * fresh event_id and the search_id correlator.
 */
export const recordSearchEvent = params => postJson('/api/search-events', params);

// ── Pending route carry (Topbar submit → SearchPage post-render record) ──

const readSession = key => {
  if (!hasWindow()) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeSession = (key, value) => {
  if (!hasWindow()) {
    return;
  }
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* storage blocked — telemetry is best-effort */
  }
};

const clearSession = key => {
  if (!hasWindow()) {
    return;
  }
  try {
    window.sessionStorage.removeItem(key);
  } catch (e) {
    /* no-op */
  }
};

/** Stash the route result so the post-render record can read it after navigation. */
export const setPendingRoute = pending => writeSession(PENDING_ROUTE_KEY, pending);

/** Read the pending route without clearing it (returns null if expired/absent). */
export const peekPendingRoute = () => {
  const pending = readSession(PENDING_ROUTE_KEY);
  if (!pending) {
    return null;
  }
  if (pending.ts && Date.now() - pending.ts > PENDING_ROUTE_TTL_MS) {
    clearSession(PENDING_ROUTE_KEY);
    return null;
  }
  return pending;
};

export const clearPendingRoute = () => clearSession(PENDING_ROUTE_KEY);

// ── Story 8.11 — result_count trustworthiness ───────────────────────────────

/**
 * The `search_log.result_count_status` vocabulary. These four strings are fixed
 * in THREE places and must agree byte-for-byte, lowercase, in all of them:
 *
 *   1. the DB CHECK `search_log_result_count_status_chk` (tph-api migration);
 *   2. this client, which is the only writer of `measured` / `unmeasured` / `error`;
 *   3. the Airtable single-select `Result Count Status` (fld50kjBooWmRl0ti),
 *      whose four options are already created and read-back-verified.
 *
 * ⚠️ Airtable typecast is off base-wide, so a FIFTH value — or a casing drift —
 * does not fail loudly at write time: it 422s at Airtable-mirror time, hours
 * later, and surfaces as missing analytics rather than a failed deploy. Adding a
 * value costs a migration AND an Airtable UI edit (`update_field` cannot add an
 * option to an existing select). Do not extend this list casually.
 *
 * `unverified_legacy` is the fourth value; it is written ONLY by the migration's
 * backfill and never by this client, so it is deliberately absent here.
 */
export const RESULT_COUNT_STATUS = {
  MEASURED: 'measured',
  UNMEASURED: 'unmeasured',
  ERROR: 'error',
};

/**
 * `search_log.searched_at` — when the search was DISPATCHED, as ISO-8601.
 *
 * Sourced from the stashed `pending.ts` (stamped in searchSubmit.js before the
 * route push), NOT from the clock at record time.
 *
 * ⚠️ DISPATCHED, NOT SUBMITTED — and the difference is not always small.
 * `searchSubmit.js` stamps `ts` inside `setPendingRoute`, which runs AFTER
 * `await routeSearch(...)`. So this is the moment the /s URL was pushed, not the
 * moment the buyer hit enter, and on an `ai_iso` query the gap is the whole
 * Anthropic parse (tph-api's ANTHROPIC_SEARCH_PARSE_TIMEOUT_MS, default 8s;
 * this client aborts at 10s). Consequence downstream: `created_at - searched_at`
 * UNDERSTATES the abandon-flush lag by the route latency, and understates it
 * more on `ai_iso` than on `keyword` — treat it as a lower bound, never as
 * time-to-abandonment. Nothing about ordering changes: `ts` is still monotonic
 * within a session and still precedes the row's own INSERT.
 *
 * It is a column of its own rather than an override of `created_at`
 * because `created_at` must keep meaning
 * "when the row was written" for EVERY row: Watchpoint #6 windows on it and V1
 * groups by it, and a column whose meaning varies from row to row is the exact
 * disease this story exists to cure. Keeping both also makes the abandon-flush
 * lag (`created_at - searched_at`) measurable instead of erased.
 *
 * @param {{ts?: number}} pending the stashed pending route
 * @returns {string} ISO-8601 timestamp
 */
export const searchedAtIso = pending => {
  const ts = pending && typeof pending.ts === 'number' ? pending.ts : Date.now();
  return new Date(ts).toISOString();
};

/**
 * Story 8.11 (AC2b) — flush an UNCONSUMED pending route as an `unmeasured` row.
 *
 * A pending route that is still in sessionStorage was provably never recorded:
 * `clearPendingRoute()` is the only other remover of the key and it runs
 * immediately before the completion path's POST. So if this function finds one,
 * that search was submitted and abandoned before its results ever landed.
 *
 * Without this, AC1's completed-search gate would reintroduce SUPPRESSION by the
 * back door — an abandoned search would write NO row where it previously wrote a
 * fabricated one, which lands hardest on the paid `ai_iso` rows whose Anthropic
 * cost is carried on the row. Writing the row with an honest status keeps every
 * route-time column (routing mix, intent-rule distribution, spend) intact.
 *
 * ⚠️ CONSUME-ONCE / AC3. The peek and the clear are both synchronous
 * sessionStorage operations with no await between them, and JS in a tab is
 * single-threaded — so this function and the completion path cannot interleave.
 * Whichever clears first makes the other's peek return null, and the other emits
 * nothing. Exactly one row per stashed pending route, never two.
 *
 * ⚠️ `latency_ms` is NULL, not 0. Nothing was measured on this path, and a 0
 * would land in the sub-100ms bucket that is the story's own success measure —
 * manufacturing the population it claims to have removed.
 *
 * ⚠️ STATED LIMIT: this is a FLOOR on abandonment, not a measure of it. The
 * flush only fires when a NEXT search is submitted, so session-ending
 * abandonment (tab close, hard navigation, giving up) writes nothing at all, and
 * `peekPendingRoute` silently discards anything older than PENDING_ROUTE_TTL_MS
 * before a flush could see it. Never quote a count of `unmeasured` rows as "the
 * abandonment rate".
 *
 * Fire-and-forget: never awaited, never blocks the caller's navigation.
 *
 * @returns {boolean} true if an abandoned pending route was found and flushed
 */
export const flushAbandonedSearch = () => {
  const pending = peekPendingRoute();
  if (!pending) {
    return false;
  }

  // Consume BEFORE the POST so a failed request cannot leave a route to be
  // flushed twice, and so the completion path can never also claim it.
  clearPendingRoute();

  const body = {
    raw_query: pending.raw_query,
    visitor_session_id: pending.visitor_session_id,
    routed_to: pending.routed_to,
    routing_reason: pending.routing_reason,
    route_context: pending.route_context,
    result_count: null,
    result_count_status: RESULT_COUNT_STATUS.UNMEASURED,
    latency_ms: null,
    searched_at: searchedAtIso(pending),
  };
  if (pending.user_uuid) {
    body.user_uuid = pending.user_uuid;
  }

  recordSearch(body).catch(() => {
    /* fire-and-forget — telemetry must never surface to the buyer */
  });

  return true;
};

/**
 * The most recently recorded search, for subsequent events. Shape:
 * `{ search_id, raw_query, started_at, first_click_recorded? }`. `started_at` is
 * the search-start timestamp (ms) used to compute first-click latency.
 */
export const setCurrentSearch = current => writeSession(CURRENT_SEARCH_KEY, current);
export const getCurrentSearch = () => readSession(CURRENT_SEARCH_KEY);

/**
 * Drop the stored current-search handle so subsequent clicks/chips aren't
 * mis-attributed to a stale search_id (e.g. after a failed /search/record).
 */
export const clearCurrentSearch = () => clearSession(CURRENT_SEARCH_KEY);

/**
 * Fire a `click` /search/events telemetry event for a result clicked within a
 * recorded search (the click-through signal). Fire-and-forget — never blocks or
 * delays navigation to the listing.
 *
 * First-click semantics (§2.7): `time_to_first_click_seconds` is included ONLY
 * on the first result click of the search; the client tracks "first click"
 * locally via a flag alongside the stored search_id + start timestamp. Later
 * clicks omit it (the backend still records the listing via append-dedupe). The
 * backend's `time_to_first_click_seconds` write is a plain SET, so a re-delivered
 * first click carrying the same value stays idempotent.
 *
 * No stored search_id (e.g. a listing viewed outside a tracked search) → no fire.
 *
 * @param {string} listingUuid the clicked listing's uuid
 */
export const recordListingClick = listingUuid => {
  if (!hasWindow() || !listingUuid) {
    return;
  }
  const current = getCurrentSearch();
  if (!current || current.search_id == null) {
    return; // not a tracked search → do not fire
  }

  const payload = { clicked_listing_uuid: listingUuid };

  if (!current.first_click_recorded) {
    if (typeof current.started_at === 'number') {
      payload.time_to_first_click_seconds = Math.max(
        0,
        Math.round((Date.now() - current.started_at) / 1000)
      );
    }
    // Mark first-click consumed so later clicks omit the timing.
    setCurrentSearch({ ...current, first_click_recorded: true });
  }

  recordSearchEvent({
    event_id: generateUuid(),
    event_type: 'click',
    search_id: current.search_id,
    occurred_at: new Date().toISOString(),
    payload,
  }).catch(() => {
    /* fire-and-forget */
  });
};
